<template>
  <div class="segment-cover-panel">
    <div class="segment-cover-panel__header">
      <span>统一封面</span>
      <el-switch
        :value="enabled"
        :disabled="disabled"
        @input="$emit('update:enabled', $event)"
      />
    </div>
    <p class="segment-cover-panel__hint">
      按抖音竖屏封面标准输出 1080×1920（9:16）JPG。建议使用无播放按钮的底图，或点击下方「从视频截取底图」。
    </p>
    <div v-if="enabled" class="segment-cover-panel__body">
      <div class="cover-upload-col">
        <el-upload
          class="cover-uploader"
          action="#"
          :show-file-list="false"
          :disabled="disabled"
          accept="image/jpeg,image/png,image/webp"
          :http-request="handleUploadRequest"
        >
          <img v-if="previewUrl" :src="previewUrl" class="cover-preview" alt="封面预览" />
          <i v-else class="el-icon-plus cover-uploader-icon"></i>
        </el-upload>
        <el-button
          size="mini"
          type="primary"
          plain
          :disabled="disabled || !projectId || extracting"
          :loading="extracting"
          @click="$emit('extract-from-video')"
        >
          从视频截取底图
        </el-button>
      </div>
      <div class="cover-fields">
        <el-form label-width="88px" size="small">
          <el-form-item label="标题">
            <el-input
              :value="title"
              :disabled="disabled"
              maxlength="64"
              show-word-limit
              placeholder="如：电影解说"
              @input="$emit('update:title', $event)"
            />
          </el-form-item>
          <el-form-item label="集数">
            <el-checkbox
              :value="episodeAuto"
              :disabled="disabled"
              @input="$emit('update:episodeAuto', $event)"
            >
              按段号自动生成（1、2、3…）
            </el-checkbox>
          </el-form-item>
          <el-form-item label="文字颜色">
            <div class="color-picker-row">
              <el-color-picker
                v-model="textColorProxy"
                color-format="hex"
                :disabled="disabled"
              />
              <span class="color-value">{{ textColor || '#FFFFFF' }}</span>
            </div>
          </el-form-item>
          <el-form-item label="文字大小">
            <el-select
              :value="fontSize"
              :disabled="disabled"
              @input="$emit('update:fontSize', $event)"
            >
              <el-option
                v-for="item in fontSizeOptions"
                :key="item.value"
                :label="item.label"
                :value="item.value"
              />
            </el-select>
          </el-form-item>
          <el-form-item label="文字描边">
            <el-switch
              :value="textStroke"
              :disabled="disabled"
              @input="$emit('update:textStroke', $event)"
            />
          </el-form-item>
          <el-form-item v-if="textStroke" label="描边颜色">
            <div class="color-picker-row">
              <el-color-picker
                v-model="textStrokeColorProxy"
                color-format="hex"
                :disabled="disabled"
              />
              <span class="color-value">{{ textStrokeColor || '#000000' }}</span>
            </div>
          </el-form-item>
        </el-form>
      </div>
    </div>
  </div>
</template>

<script>
import { normalizeHexColor, normalizeStrokeHexColor } from '../utils/color';
import { COVER_FONT_SIZE_DEFAULT, COVER_FONT_SIZE_OPTIONS } from '../constants/textFontSizes';

export default {
  name: 'SegmentCoverPanel',
  props: {
    enabled: { type: Boolean, default: false },
    title: { type: String, default: '' },
    episodeAuto: { type: Boolean, default: true },
    textColor: { type: String, default: '#FFFFFF' },
    textStroke: { type: Boolean, default: true },
    textStrokeColor: { type: String, default: '#000000' },
    fontSize: { type: Number, default: COVER_FONT_SIZE_DEFAULT },
    previewUrl: { type: String, default: '' },
    projectId: { type: String, default: '' },
    extracting: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
  },
  data() {
    return {
      fontSizeOptions: COVER_FONT_SIZE_OPTIONS,
    };
  },
  computed: {
    textColorProxy: {
      get() {
        return this.textColor || '#FFFFFF';
      },
      set(value) {
        this.$emit('update:textColor', normalizeHexColor(value, '#FFFFFF'));
      },
    },
    textStrokeColorProxy: {
      get() {
        return this.textStrokeColor || '#000000';
      },
      set(value) {
        this.$emit('update:textStrokeColor', normalizeStrokeHexColor(value));
      },
    },
  },
  methods: {
    handleUploadRequest({ file }) {
      const previewUrl = URL.createObjectURL(file);
      this.$emit('cover-file-change', { file, previewUrl });
    },
  },
};
</script>

<style scoped>
.segment-cover-panel {
  margin-top: 16px;
  padding: 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fafafa;
}
.segment-cover-panel__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-weight: 600;
  font-size: 14px;
}
.segment-cover-panel__hint {
  margin: 8px 0 0;
  font-size: 12px;
  color: #6b7280;
  line-height: 1.5;
}
.segment-cover-panel__body {
  display: flex;
  gap: 16px;
  margin-top: 12px;
  flex-wrap: wrap;
}
.cover-uploader {
  /* 9:16 抖音封面比例预览 */
  width: 108px;
  height: 192px;
  border: 1px dashed #d9d9d9;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
}
.cover-uploader-icon {
  font-size: 28px;
  color: #8c939d;
}
.cover-preview {
  width: 100%;
  height: 100%;
  object-fit: contain;
  background: #111827;
}
.cover-fields {
  flex: 1;
  min-width: 240px;
}
.cover-upload-col {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}
.color-picker-row {
  display: flex;
  align-items: center;
  gap: 10px;
}
.color-value {
  font-size: 13px;
  color: #374151;
  font-family: monospace;
}
</style>
